/**
 * Les présences de la garderie depuis le bureau (Gestion › Présences) : ce que
 * l'écran sait sans la base.
 *
 * La base rend la semaine d'une école en un document (`garderie_semaine`,
 * migration `20261101200000_la_vue_des_presences_de_la_garderie`) : les cinq
 * jours, les plages de l'année, les enfants présents dans l'école cette
 * semaine-là et leurs présences vivantes. Un ✓ ou un ✗ passe par
 * `garderie_pointer`, et c'est la base qui décide (rôle, école, année, avenir,
 * mois facturé). Ici : la forme des données, les colonnes de la grille (quelle
 * plage tourne quel jour), les totaux, les filtres, les dates, les textes.
 * PROPOSITION TECHNIQUE. [cdv:garderie]
 */
import { lireHabitudes, type FiltreDeGroupe, type Groupe, type LecturePointage } from './pointage';

export interface JourDeLaSemaine {
  /** `AAAA-MM-JJ`. */
  date: string;
  /** 1 = lundi … 5 = vendredi. */
  isodow: number;
  ferie: string | null;
  horsAnnee: boolean;
  futur: boolean;
  etatMois: 'ouvert' | 'brouillon' | 'facture' | null;
}

export interface PlageDeLaSemaine {
  id: string;
  code: string;
  libelle: string;
  heureDebut: string;
  heureFin: string;
  jours: number[];
  ordre: number;
  impliquePlageId: string | null;
}

export interface PresenceDeLaSemaine {
  date: string;
  plageId: string;
}

export interface EnfantDeLaSemaine {
  id: string;
  nom: string;
  prenom: string;
  classe: string | null;
  groupe: Groupe | null;
  rentreLeMidi: boolean;
  dateEntree: string | null;
  dateSortie: string | null;
  formule: string | null;
  presences: PresenceDeLaSemaine[];
  /** Par plage, les jours de présence dans les 8 semaines avant le lundi (`20261102180000`) : la même fenêtre que le téléphone. */
  habitudes: Record<string, number>;
}

export interface SemaineDeGarderie {
  ecole: { id: string; nom: string; code: string };
  anneeScolaire: number;
  /** `AAAA-MM-JJ`, le lundi. */
  lundi: string;
  aujourdHui: string;
  jours: JourDeLaSemaine[];
  plages: PlageDeLaSemaine[];
  enfants: EnfantDeLaSemaine[];
}

/* ------------------------------------------------------------------------ */
/*  Lire ce que la base rend                                                 */
/* ------------------------------------------------------------------------ */

const texte = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

export function lireSemaine(brut: unknown): SemaineDeGarderie {
  const d = (brut ?? {}) as Record<string, unknown>;
  const ecole = (d.ecole ?? {}) as Record<string, unknown>;
  const jours = ((d.jours ?? []) as Record<string, unknown>[]).map((j) => ({
    date: String(j.date),
    isodow: Number(j.isodow),
    ferie: texte(j.ferie),
    horsAnnee: j.hors_annee === true,
    futur: j.futur === true,
    etatMois: (texte(j.etat_mois) as JourDeLaSemaine['etatMois']) ?? null,
  }));
  const plages = ((d.plages ?? []) as Record<string, unknown>[]).map((p) => ({
    id: String(p.id),
    code: String(p.code),
    libelle: String(p.libelle),
    heureDebut: String(p.heure_debut),
    heureFin: String(p.heure_fin),
    jours: ((p.jours ?? []) as unknown[]).map(Number),
    ordre: Number(p.ordre ?? 0),
    impliquePlageId: texte(p.implique_plage_id),
  }));
  const enfants = ((d.enfants ?? []) as Record<string, unknown>[]).map((e) => ({
    id: String(e.id),
    nom: String(e.nom),
    prenom: String(e.prenom),
    classe: texte(e.classe),
    groupe: (texte(e.groupe) as Groupe | null) ?? null,
    rentreLeMidi: e.rentre_le_midi === true,
    dateEntree: texte(e.date_entree),
    dateSortie: texte(e.date_sortie),
    formule: texte(e.formule),
    presences: ((e.presences ?? []) as Record<string, unknown>[]).map((p) => ({ date: String(p.date), plageId: String(p.plage_id) })),
    habitudes: lireHabitudes(e.habitudes),
  }));
  return {
    ecole: { id: String(ecole.id ?? ''), nom: String(ecole.nom ?? ''), code: String(ecole.code ?? '') },
    anneeScolaire: Number(d.annee_scolaire),
    lundi: String(d.lundi),
    aujourdHui: String(d.aujourd_hui),
    jours,
    plages,
    enfants,
  };
}

/**
 * Une date de la semaine, dans la forme de la liste du téléphone
 * (`LecturePointage`) : l'écran de pointage de l'animateur sert aussi à Gestion
 * (le tableau de bord y mène). Les enfants entrés après la date ou sortis avant
 * n'y sont pas ; une date hors de l'année scolaire n'a pas de plage.
 */
export function lectureDuJour(s: SemaineDeGarderie, date: string): LecturePointage {
  const jour = s.jours.find((j) => j.date === date) ?? null;
  return {
    codeRequis: false,
    ecole: s.ecole,
    anneeScolaire: s.anneeScolaire,
    date,
    aujourdHui: date === s.aujourdHui,
    ferie: jour?.ferie ?? null,
    moisFacture: jour?.etatMois === 'facture',
    plageProposee: null,
    plages: !jour || jour.horsAnnee
      ? []
      : s.plages.map((p) => ({ id: p.id, code: p.code, libelle: p.libelle, heureDebut: p.heureDebut, heureFin: p.heureFin, jours: p.jours })),
    enfants: s.enfants
      .filter((e) => (!e.dateEntree || e.dateEntree <= date) && (!e.dateSortie || e.dateSortie >= date))
      .map((e) => ({
        id: e.id,
        prenom: e.prenom,
        nom: e.nom,
        groupe: e.groupe,
        classe: e.classe,
        presences: e.presences.filter((p) => p.date === date).map((p) => p.plageId),
        habitudes: e.habitudes,
      })),
  };
}

/* ------------------------------------------------------------------------ */
/*  Les dates                                                                */
/* ------------------------------------------------------------------------ */

const jourUtc = (iso: string): Date => {
  const [a, m, j] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, j));
};
const enIso = (d: Date): string => d.toISOString().slice(0, 10);

/** Le lundi de la semaine d'une date (`AAAA-MM-JJ`). */
export function lundiDe(iso: string): string {
  const d = jourUtc(iso);
  const decalage = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - decalage);
  return enIso(d);
}

export function decalerDeSemaines(lundi: string, n: number): string {
  const d = jourUtc(lundi);
  d.setUTCDate(d.getUTCDate() + 7 * n);
  return enIso(d);
}

export function aujourdHuiIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const JOURS_COURTS = ['', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS_LONGS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** « Lun 7/9 ». */
export function jourCourt(j: JourDeLaSemaine): string {
  const d = jourUtc(j.date);
  return `${JOURS_COURTS[j.isodow]} ${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
}

/** « 7 – 11 septembre 2026 », ou « 31 août – 4 septembre 2026 ». */
export function semaineEnClair(lundi: string): string {
  const a = jourUtc(lundi);
  const b = jourUtc(decalerDeSemaines(lundi, 0));
  b.setUTCDate(b.getUTCDate() + 4);
  const memeMois = a.getUTCMonth() === b.getUTCMonth();
  const debut = memeMois ? String(a.getUTCDate()) : `${a.getUTCDate()} ${MOIS_LONGS[a.getUTCMonth()]}`;
  return `${debut} – ${b.getUTCDate()} ${MOIS_LONGS[b.getUTCMonth()]} ${b.getUTCFullYear()}`;
}

/** `JJ/MM/AAAA`. */
export function dateEnClair(iso: string): string {
  const [a, m, j] = iso.split('-');
  return `${j}/${m}/${a}`;
}

/* ------------------------------------------------------------------------ */
/*  La grille                                                                */
/* ------------------------------------------------------------------------ */

export interface ColonneDeLaGrille {
  jour: JourDeLaSemaine;
  plage: PlageDeLaSemaine;
  cle: string;
  /** Pas de ✓ possible : férié, avenir, hors année ou mois facturé. */
  verrouillee: boolean;
  motif: string | null;
}

export const cleCellule = (date: string, plageId: string): string => `${date}|${plageId}`;

/** Pourquoi une case ne se coche pas, ou `null`. */
export function motifDeVerrou(j: JourDeLaSemaine): string | null {
  if (j.horsAnnee) return 'hors de l’année scolaire';
  if (j.futur) return 'à venir';
  if (j.etatMois === 'facture') return 'mois facturé';
  return null;
}

/** Les colonnes : pour chaque jour, les plages qui tournent ce jour-là, dans l'ordre des plages. */
export function colonnesDeLaSemaine(s: SemaineDeGarderie, jours: JourDeLaSemaine[] = s.jours): ColonneDeLaGrille[] {
  const plages = [...s.plages].sort((a, b) => a.ordre - b.ordre || a.heureDebut.localeCompare(b.heureDebut));
  const out: ColonneDeLaGrille[] = [];
  for (const jour of jours) {
    for (const plage of plages) {
      if (!plage.jours.includes(jour.isodow)) continue;
      const motif = motifDeVerrou(jour);
      out.push({ jour, plage, cle: cleCellule(jour.date, plage.id), verrouillee: motif !== null, motif });
    }
  }
  return out;
}

export function estPresent(e: EnfantDeLaSemaine, date: string, plageId: string): boolean {
  return e.presences.some((p) => p.date === date && p.plageId === plageId);
}

/** Le nombre d'enfants pointés par colonne (clé → nombre). */
export function totauxParColonne(enfants: EnfantDeLaSemaine[], colonnes: ColonneDeLaGrille[]): Map<string, number> {
  const t = new Map<string, number>();
  for (const c of colonnes) t.set(c.cle, 0);
  for (const e of enfants) {
    for (const p of e.presences) {
      const cle = cleCellule(p.date, p.plageId);
      if (t.has(cle)) t.set(cle, (t.get(cle) ?? 0) + 1);
    }
  }
  return t;
}

/** Les ✓ d'un enfant dans la semaine (toutes plages). */
export function totalDeLEnfant(e: EnfantDeLaSemaine): number {
  return e.presences.length;
}

/** Les ✓ de chaque jour, toutes plages confondues, et la part de « sans aucune présence ». */
export function totauxDeLaSemaine(s: SemaineDeGarderie): { parJour: Map<string, number>; presences: number; enfantsPointes: number } {
  const parJour = new Map<string, number>();
  for (const j of s.jours) parJour.set(j.date, 0);
  let presences = 0;
  let enfantsPointes = 0;
  for (const e of s.enfants) {
    if (e.presences.length > 0) enfantsPointes += 1;
    for (const p of e.presences) {
      presences += 1;
      if (parJour.has(p.date)) parJour.set(p.date, (parJour.get(p.date) ?? 0) + 1);
    }
  }
  return { parJour, presences, enfantsPointes };
}

/** La semaine après un ✓ ou un ✗, sans attendre la base (la base confirme ou corrige). */
export function appliquerLePointage(s: SemaineDeGarderie, enfantId: string, date: string, plageId: string, present: boolean): SemaineDeGarderie {
  return {
    ...s,
    enfants: s.enfants.map((e) => {
      if (e.id !== enfantId) return e;
      const deja = estPresent(e, date, plageId);
      if (present === deja) return e;
      return {
        ...e,
        presences: present
          ? [...e.presences, { date, plageId }]
          : e.presences.filter((p) => !(p.date === date && p.plageId === plageId)),
      };
    }),
  };
}

/* ------------------------------------------------------------------------ */
/*  Les filtres                                                              */
/* ------------------------------------------------------------------------ */

export interface FiltresDeLaGrille {
  groupe: FiltreDeGroupe;
  /** Le libellé exact d'une classe, ou `''` pour toutes. */
  classe: string;
  recherche: string;
  /** Ne montrer que les enfants pointés au moins une fois dans la semaine. */
  pointesSeulement: boolean;
}

export const FILTRES_PAR_DEFAUT: FiltresDeLaGrille = { groupe: 'tous', classe: '', recherche: '', pointesSeulement: false };

const simplifier = (t: string): string =>
  t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

/** Les classes présentes dans la semaine, dans l'ordre naturel (M avant P, puis le libellé). */
export function classesDeLaSemaine(enfants: EnfantDeLaSemaine[]): string[] {
  const vues = new Set<string>();
  for (const e of enfants) if (e.classe) vues.add(e.classe);
  return [...vues].sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }));
}

export function filtrerLesEnfants(enfants: EnfantDeLaSemaine[], f: FiltresDeLaGrille): EnfantDeLaSemaine[] {
  const q = simplifier(f.recherche);
  return enfants.filter((e) => {
    if (f.groupe !== 'tous' && e.groupe !== f.groupe) return false;
    if (f.classe && e.classe !== f.classe) return false;
    if (f.pointesSeulement && e.presences.length === 0) return false;
    if (q && !simplifier(`${e.prenom} ${e.nom} ${e.nom} ${e.prenom} ${e.classe ?? ''}`).includes(q)) return false;
    return true;
  });
}

/* ------------------------------------------------------------------------ */
/*  L'export                                                                 */
/* ------------------------------------------------------------------------ */

const champCsv = (v: string | number | null | undefined): string => {
  const t = v === null || v === undefined ? '' : String(v);
  return /[";\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};

/** La grille en CSV (point-virgule) : un enfant par ligne, une colonne par (jour, plage), 1 = présent. */
export function semaineEnCsv(s: SemaineDeGarderie, enfants: EnfantDeLaSemaine[] = s.enfants): string {
  const colonnes = colonnesDeLaSemaine(s);
  const entete = ['Nom', 'Prénom', 'Classe', 'Groupe', 'Formule', ...colonnes.map((c) => `${dateEnClair(c.jour.date)} ${c.plage.code}`), 'Total'];
  const lignes = enfants.map((e) => [
    e.nom,
    e.prenom,
    e.classe ?? '',
    e.groupe ?? '',
    e.formule ?? '',
    ...colonnes.map((c) => (estPresent(e, c.jour.date, c.plage.id) ? '1' : '')),
    String(totalDeLEnfant(e)),
  ]);
  const totaux = totauxParColonne(enfants, colonnes);
  const pied = ['Total', '', '', '', '', ...colonnes.map((c) => String(totaux.get(c.cle) ?? 0)), String(enfants.reduce((n, e) => n + totalDeLEnfant(e), 0))];
  return [entete, ...lignes, pied].map((l) => l.map(champCsv).join(';')).join('\n');
}
